import {InputRule, mergeAttributes, Node, nodePasteRule} from "@tiptap/core";
import type {Editor, InputRuleMatch, PasteRuleMatch} from "@tiptap/core";
import type {Node as ProseMirrorNode} from "@tiptap/pm/model";
import {Plugin, PluginKey} from "@tiptap/pm/state";
import {Decoration, DecorationSet} from "@tiptap/pm/view";
import {ReactNodeViewRenderer} from "@tiptap/react";
import {VariableChip} from "../chip/VariableChip";
import {DEFAULT_CONTENT_LANGUAGE} from "../model/resolution";
import type {PickerVariable, VariableCatalogue} from "../model/types";
import {
    DEFAULT_FALLBACK,
    tokenFor,
    VARIABLE_NODE_NAME,
    variableAttributes,
    variableTokenPattern,
} from "../model/variableToken";
import {createVariableCatalogueStore, type VariableCatalogueStore} from "./variableCatalogueStore";

export interface VariableNodeOptions {
    /** The editor's catalogue. `variableExtensions` creates one per editor and hands it to both extensions. */
    store?: VariableCatalogueStore;
}

export interface VariableNodeStorage {
    store: VariableCatalogueStore;
    /** Read by `tiptap-markdown`, for a host that stores markdown: the chip is written as its token. */
    markdown: {serialize: (state: {write: (text: string) => void}, node: ProseMirrorNode) => void};
}

const tokenOf = (node: ProseMirrorNode) =>
    tokenFor(node.attrs.key, {format: node.attrs.format, fallback: node.attrs.fallback});

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        variable: {
            /** Swaps the catalogue the picker offers and the chips validate against; chips repaint. */
            setVariableCatalogue: (catalogue: VariableCatalogue) => ReturnType;
        };
    }
}

const EMPTY_CATALOGUE: VariableCatalogue = {variables: [], preview: {language: DEFAULT_CONTENT_LANGUAGE}};

const VariableSelectionPluginKey = new PluginKey("variableSelection");

/*
    Typed right after "chip + space", these take the space's place: the picker puts a space after a chip to write on
    in, and "Jung !" is never meant. Not the hyphen or quotes, which after a space start something new.
 */
const CLOSES_UP_WHEN_TYPED = /^[.,;:!?…)\]]$/u;

/*
    The schema is read without an editor (and then with the extension's own storage, which is built
    afresh each time it is read), so the editor's store is looked up first: a bare node swapped with
    `setVariableCatalogue` must validate against what was swapped in.
 */
const storeOf = (editor: Editor | undefined, own: VariableCatalogueStore) =>
    (editor?.storage as unknown as Record<string, Partial<VariableNodeStorage> | undefined> | undefined)?.[
        VARIABLE_NODE_NAME
    ]?.store ?? own;

/** Every known token in `text`, with the attributes it becomes. Unknown keys stay the text they are. */
const knownTokens = (text: string, variables: readonly PickerVariable[]) =>
    Array.from(text.matchAll(variableTokenPattern())).flatMap(match => {
        const attributes = match.index === undefined ? false : variableAttributes(variables, match[0]);
        return attributes ? [{match, attributes}] : [];
    });

/** One inline atom carrying key, fallback and format. `{{key:format|fallback}}` is the text form. */
export const VariableNode = Node.create<VariableNodeOptions, VariableNodeStorage>({
    name: VARIABLE_NODE_NAME,
    group: "inline",
    inline: true,
    atom: true,
    selectable: true,
    marks: "_",

    addOptions() {
        return {store: undefined};
    },

    // The store, not a copy of the catalogue: a host swaps what is in it without rebuilding the editor and losing undo.
    addStorage() {
        return {
            store: this.options.store ?? createVariableCatalogueStore(EMPTY_CATALOGUE),
            // The same text form as `renderText`; `variableTokensToHtml` turns it back into chips on load.
            markdown: {serialize: (state, node) => state.write(tokenOf(node))},
        };
    },

    addCommands() {
        return {
            setVariableCatalogue: catalogue => () => {
                this.storage.store.set(catalogue);
                return true;
            },
        };
    },

    addAttributes() {
        return {
            key: {
                default: null,
                parseHTML: element => element.getAttribute("data-variable"),
                renderHTML: attributes => ({"data-variable": attributes.key}),
            },
            fallback: {
                default: DEFAULT_FALLBACK,
                parseHTML: element => element.getAttribute("data-fallback") ?? DEFAULT_FALLBACK,
                renderHTML: attributes => ({"data-fallback": attributes.fallback ?? ""}),
            },
            format: {
                default: "",
                parseHTML: element => element.getAttribute("data-format") ?? "",
                renderHTML: attributes => (attributes.format ? {"data-format": attributes.format} : {}),
            },
        };
    },

    parseHTML() {
        const editor = this.editor;
        const own = this.storage.store;

        return [
            {
                tag: "span[data-variable]",
                // Pasted HTML is held to the same catalogue as typed and pasted text.
                getAttrs: element => {
                    const key = (element as HTMLElement).getAttribute("data-variable");
                    const {variables} = storeOf(editor, own).get();

                    return variables.some(variable => variable.key === key) ? null : false;
                },
            },
        ];
    },

    renderHTML({HTMLAttributes, node}) {
        return ["span", mergeAttributes(HTMLAttributes), tokenOf(node)];
    },

    renderText({node}) {
        return tokenOf(node);
    },

    addInputRules() {
        return [
            new InputRule({
                // The token that ends where the caret is, if there is one.
                find: (text): InputRuleMatch | null => {
                    const match = Array.from(text.matchAll(variableTokenPattern())).pop();
                    if (!match || match.index === undefined || match.index + match[0].length !== text.length) {
                        return null;
                    }

                    return {index: match.index, text: match[0], match};
                },
                handler: ({state, range, match}) => {
                    const attributes = variableAttributes(this.storage.store.get().variables, match[0]);
                    if (!attributes) return null;

                    state.tr.replaceWith(range.from, range.to, this.type.create(attributes));
                    return undefined;
                },
            }),
        ];
    },

    addPasteRules() {
        return [
            nodePasteRule({
                /*
                    Only known tokens are matched: one unknown key answered with `false` fails the whole
                    paste rule, and the known tokens next to it would stay text.
                 */
                find: (text): PasteRuleMatch[] =>
                    knownTokens(text, this.storage.store.get().variables).map(({match, attributes}) => ({
                        index: match.index as number,
                        text: match[0],
                        match,
                        data: attributes,
                    })),
                type: this.type,
                getAttributes: match => match.data ?? {},
            }),
        ];
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: VariableSelectionPluginKey,
                props: {
                    handleTextInput(view, from, to, text) {
                        if (from !== to || from < 2 || !CLOSES_UP_WHEN_TYPED.test(text)) return false;

                        const {doc} = view.state;
                        if (doc.textBetween(from - 1, from) !== " ") return false;
                        if (doc.resolve(from - 1).nodeBefore?.type.name !== VARIABLE_NODE_NAME) return false;

                        view.dispatch(view.state.tr.insertText(text, from - 1, from));
                        return true;
                    },
                    decorations(state) {
                        const {from, to, empty} = state.selection;
                        if (empty) return null;

                        const decorations: Decoration[] = [];
                        state.doc.nodesBetween(from, to, (node, pos) => {
                            if (node.type.name !== VARIABLE_NODE_NAME) return;
                            decorations.push(
                                Decoration.node(pos, pos + node.nodeSize, {class: "variable-in-selection"}),
                            );
                        });

                        return DecorationSet.create(state.doc, decorations);
                    },
                },
            }),
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(VariableChip, {as: "span"});
    },
});

export default VariableNode;
